import {skipToken} from '@reduxjs/toolkit/query';
import {FlagProps, Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {getBusinessCaseStateFlag, getBusinessCaseTypeFlag, getCustomerName} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';

const LOADING_PLACEHOLDER = '…';

export function useBusinessCasePairedRecord(businessCaseId: string | null): PairedRecordView {
  const {
    data: businessCase,
    isLoading,
    isError,
  } = useGetBusinessCaseQuery(isNotNil(businessCaseId) ? {businessCaseId} : skipToken);

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(businessCase?.customerId) ? {customerId: businessCase.customerId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const activeOffer = businessCase?.offers?.[0];
  const isBrokerage =
    businessCase?.businessCaseInternalType === 'PURCHASE_BROKERAGE' ||
    isNotNil(businessCase?.brokerageBusinessCaseId);
  const saleVehicles = activeOffer?.saleVehicles ?? [];
  const purchaseVehicles = isBrokerage
    ? (activeOffer?.purchaseBrokerageVehicles ?? [])
    : (activeOffer?.purchaseVehicles ?? []);
  const vehicleIds = [
    ...saleVehicles.map((vehicle) => vehicle.vehicleId),
    ...purchaseVehicles.map((vehicle) => vehicle.vehicleId),
  ];

  const code =
    businessCase?.code ??
    (isLoading ? LOADING_PLACEHOLDER : i18n.t('general.notifications.notFound'));
  const customerName = getCustomerName(customer);
  const createdAtFormatted = businessCase?.createdAt
    ? formatDateTime('dateShort', parseDate(businessCase.createdAt))
    : null;
  const updatedAtFormatted =
    businessCase?.updatedAt && isNotNil(parseDate(businessCase.updatedAt))
      ? formatDateTime('dateShort', parseDate(businessCase.updatedAt))
      : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(
      isNotNil(updatedAtFormatted),
      `${i18n.t('general.labels.lastUpdated')} ${updatedAtFormatted}`
    );

  const stateFlag = isNotNil(businessCase)
    ? getBusinessCaseStateFlag(businessCase.businessCaseState)
    : undefined;
  const typeFlag = isNotNil(businessCase?.businessCaseType)
    ? getBusinessCaseTypeFlag(businessCase.businessCaseType)
    : undefined;
  const flags = [stateFlag, typeFlag].filter((flag): flag is FlagProps => isNotNil(flag));

  return {
    isLoading,
    isError,
    code,
    detailPath: isNotNil(businessCaseId)
      ? composePath(businessCaseRoutes.overview, {params: {id: businessCaseId}})
      : null,
    parameters,
    flags,
    vehicleIds,
  };
}
